// config files for languages
import ThemePageConfigFI from '../../themePageConfig_fi.json';
import ThemePageConfigSE from '../../themePageConfig_se.json';

export class ThemePageManager {
  /*
  store general Themepage related information
  like valid keys, chip texts & colors as well as
  current page's data based on language and slug.
  */

  constructor(initialData) {
    this.__allData = initialData;
    this.__lang = 'fi';
    this.__pageData = undefined;

    // list of valid slugs per language
    // also includes active=false making them accessible
    // by URL /teemat/slug. This is to make testing easier.
    // add _ to theme's key to truly disable it.
    this.allSlugs = {};
    // text and color for frontpage chips per language
    // only includes active=true
    this.allChips = {};
    // map of slugs to config key per language
    this.__slugsToKey = {};

    // go through the original data, and derive
    // needed variables
    Object.entries(this.__allData).forEach(
      ([lang, config]) => {
        const validKeys = Object.keys(config).filter(
          (key) => !key.startsWith('_'),
        );

        this.allSlugs[lang] = validKeys.map(
          (key) => config[key].slug,
        );

        // list of chips for front-page, used in SearchForEveryone.js
        // filter out inactive to hide the chips
        this.allChips[lang] = validKeys
          .filter((key) => config[key].active)
          .map((key) => ({
            chip: config[key].slug,
            // if chipColor is not defined, use lightColor
            backgroundColor:
              config[key].chipColor ??
              config[key].lightColor,
          }));

        // map of slugs to config entry keys
        this.__slugsToKey[lang] = validKeys.reduce(
          (acc, key) => {
            const { slug } = config[key];
            return { ...acc, [slug]: key };
          },
          {},
        );
      },
    );
  }

  changePage(slug, lang) {
    // change page data based on new language and slug
    // returns null on failure and pageData on success
    if (!Object.hasOwn(this.allSlugs, lang)) {
      console.error('Invalid language', lang);
      return null;
    }

    if (!this.allSlugs[lang].includes(slug)) {
      console.error('Invalid slug', slug);
      return null;
    }

    this.__lang = lang;

    const raw = this.__allData[lang][slug];
    this.__pageData = {
      ...raw,
      // overrides
      keywords: this.combineKeywords(raw.keywords),
      chipColor: raw?.chipColor ?? raw?.lightColor,
    };
    return this.__pageData;
  }

  combineKeywords(keywords) {
    // searching with keywords is case-sensitive
    // for example using "Eläimet" results only in results from 2014
    // while "eläimet" includes more recent
    // use both lowercase and capitalized words for greater coverage
    // TODO: make ElasticSearch ignore case when using keywords?
    const sharedIgnore =
      this.__allData[this.__lang]?._sharedIgnore ?? [];

    return (
      keywords
        // capitalize all words
        .map(
          (word) => word[0].toUpperCase() + word.slice(1),
        )
        // copy the same words in lowercase
        .concat(keywords.map((word) => word.toLowerCase()))
        // add general sharedIgnore
        .concat(sharedIgnore)
        // keyword filtering uses EI instead of NOT
        .map((term) => term.replaceAll('NOT', 'EI'))
    );
  }

  get pageData() {
    // data for current language and slug
    return this.__pageData;
  }
}

// define languages and configs
const managerSingleton = new ThemePageManager({
  fi: ThemePageConfigFI,
  se: ThemePageConfigSE,
});

export default managerSingleton;
